import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Profile } from '../../../shared/types';
import axiosService from '../services/axios.service';

export type QuestGroupId = 'memberOnboarding' | 'managerOnboarding' | 'mentorshipOnboarding';

export type QuestId =
    | 'memberOnboarding.createdProfilePicture'
    | 'memberOnboarding.usedSearch'
    | 'memberOnboarding.updatedDescription';

export type QuestSection = {
    [key: string]: boolean;
}

export interface ProfileQuestState {
    profileId: string;
    skippedOrCompleted: Record<QuestGroupId, boolean>;
    quests: { [key: string]: QuestSection };
}

export interface QuestDefinition {
    id: QuestId;
    title: string;
    description: string;
}

export interface QuestGroupDefinition {
    id: QuestGroupId;
    title: string;
    description: string;
    quests: QuestDefinition[];
    isEligible: (context: { profile?: Profile; hasDirectReports?: boolean }) => boolean;
}

const QUEST_GROUPS: QuestGroupDefinition[] = [
    {
        id: 'memberOnboarding',
        title: 'Member onboarding',
        description: 'Get your profile ready and discover your team.',
        quests: [
            {
                id: 'memberOnboarding.createdProfilePicture',
                title: 'Add a profile picture',
                description: 'Upload a profile photo so teammates recognize you.'
            },
            {
                id: 'memberOnboarding.usedSearch',
                title: 'Search for a teammate',
                description: 'Use the search box to find a person in your crew.'
            },
            {
                id: 'memberOnboarding.updatedDescription',
                title: 'Write a short bio',
                description: 'Add a brief description to your profile.'
            }
        ],
        isEligible: () => true
    },
    {
        id: 'managerOnboarding',
        title: 'Manager onboarding',
        description: 'Set up your management tools and team insights.',
        quests: [],
        isEligible: ({ profile, hasDirectReports }) => !!hasDirectReports || (profile?.directReportsCount || 0) > 0
    },
    {
        id: 'mentorshipOnboarding',
        title: 'Mentorship onboarding',
        description: 'Prepare to mentor and connect with mentees.',
        quests: [],
        isEligible: ({ profile }) => !!profile?.isMentoring
    }
];

const buildDefaultState = (profileId: string): ProfileQuestState => ({
    profileId,
    skippedOrCompleted: {
        memberOnboarding: false,
        managerOnboarding: false,
        mentorshipOnboarding: false
    },
    quests: {
        memberOnboarding: {
            createdProfilePicture: false,
            usedSearch: false,
            updatedDescription: false
        },
        managerOnboarding: {},
        mentorshipOnboarding: {}
    }
});

const mergeQuestState = (profileId: string, incoming?: Partial<ProfileQuestState>): ProfileQuestState => {
    const base = buildDefaultState(profileId);

    if (!incoming) {
        return base;
    }

    return {
        ...base,
        ...incoming,
        skippedOrCompleted: {
            ...base.skippedOrCompleted,
            ...(incoming.skippedOrCompleted || {})
        },
        quests: {
            ...base.quests,
            ...(incoming.quests || {}),
            memberOnboarding: {
                ...base.quests.memberOnboarding,
                ...(incoming.quests?.memberOnboarding || {})
            }
        }
    };
};

export interface UseProfileQuestsOptions {
    profile?: Profile;
    hasDirectReports?: boolean;
}

export interface QuestGroupView {
    id: QuestGroupId;
    title: string;
    description: string;
    quests: Array<QuestDefinition & { completed: boolean }>;
    completedCount: number;
    totalCount: number;
    progressPercent: number;
    isCompleted: boolean;
    isSkipped: boolean;
    isEligible: boolean;
}

export const useProfileQuests = ({ profile, hasDirectReports }: UseProfileQuestsOptions) => {
    const profileId = profile?._id || '';
    const crewSlug = profile?.crewSlug || '';

    const pendingRequests = useRef(0);

    const [questState, setQuestState] = useState<ProfileQuestState>(() => buildDefaultState(profileId));
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let isActive = true;

        if (!profileId || !crewSlug) {
            setQuestState(buildDefaultState(profileId));
            return;
        }

        const fetchState = async () => {
            try {
                setIsLoading(true);
                const { data } = await axiosService.get(`/c/${crewSlug}/profile-quests/me`);
                if (!isActive) {
                    return;
                }
                setQuestState(mergeQuestState(profileId, data?.data || data));
            } catch {
                if (isActive) {
                    setQuestState(buildDefaultState(profileId));
                }
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        fetchState();

        return () => {
            isActive = false;
        };
    }, [profileId, crewSlug]);

    const markQuestCompleted = useCallback(async (questId: QuestId, value: boolean = true) => {
        const [groupId, questKey] = questId.split('.') as [QuestGroupId, string];
        if (!groupId || !questKey || !profileId || !crewSlug) {
            return;
        }

        pendingRequests.current += 1;

        setQuestState(prev => {
            if (!prev.quests[groupId]) {
                return prev;
            }

            return {
                ...prev,
                quests: {
                    ...prev.quests,
                    [groupId]: {
                        ...prev.quests[groupId],
                        [questKey]: value
                    }
                }
            };
        });

        try {
            const { data } = await axiosService.patch(`/c/${crewSlug}/profile-quests/me/quest`, {
                questId,
                value
            });

            setQuestState(prev => mergeQuestState(profileId, data?.data || data || prev));
        } catch {
            // Ignore API errors for now
        } finally {
            pendingRequests.current = Math.max(pendingRequests.current - 1, 0);
        }
    }, [profileId, crewSlug]);

    const setGroupSkippedOrCompleted = useCallback(async (groupId: QuestGroupId, value: boolean) => {
        if (!groupId || !profileId || !crewSlug) {
            return;
        }

        pendingRequests.current += 1;

        setQuestState(prev => ({
            ...prev,
            skippedOrCompleted: {
                ...prev.skippedOrCompleted,
                [groupId]: value
            }
        }));

        try {
            const { data } = await axiosService.patch(`/c/${crewSlug}/profile-quests/me/group`, {
                groupId,
                value
            });

            setQuestState(prev => mergeQuestState(profileId, data?.data || data || prev));
        } catch {
            // Ignore API errors for now
        } finally {
            pendingRequests.current = Math.max(pendingRequests.current - 1, 0);
        }
    }, [profileId, crewSlug]);

    useEffect(() => {
        if (!profileId) {
            return;
        }

        if (profile?.profilePicture && !questState.quests.memberOnboarding.createdProfilePicture) {
            markQuestCompleted('memberOnboarding.createdProfilePicture');
        }

        if (profile?.bio?.trim() && !questState.quests.memberOnboarding.updatedDescription) {
            markQuestCompleted('memberOnboarding.updatedDescription');
        }
    }, [profileId, profile?.profilePicture, profile?.bio, questState.quests.memberOnboarding.createdProfilePicture, questState.quests.memberOnboarding.updatedDescription, markQuestCompleted]);

    const groupViews = useMemo<QuestGroupView[]>(() => {
        return QUEST_GROUPS.map(group => {
            const isEligible = group.isEligible({ profile, hasDirectReports });
            const isSkipped = questState.skippedOrCompleted[group.id];

            const quests = group.quests.map(quest => {
                const [groupId, questKey] = quest.id.split('.') as [QuestGroupId, string];
                const completed = Boolean(questState.quests[groupId]?.[questKey]);

                return {
                    ...quest,
                    completed
                };
            });

            const completedCount = quests.filter(q => q.completed).length;
            const totalCount = quests.length;
            const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
            const isCompleted = isSkipped || (totalCount > 0 && completedCount === totalCount);

            return {
                id: group.id,
                title: group.title,
                description: group.description,
                quests,
                completedCount,
                totalCount,
                progressPercent,
                isCompleted,
                isSkipped,
                isEligible
            };
        });
    }, [profile, hasDirectReports, questState]);

    const eligibleGroups = groupViews.filter(group => group.isEligible && group.totalCount > 0);

    const totals = useMemo(() => {
        const totalCount = eligibleGroups.reduce((acc, group) => acc + group.totalCount, 0);
        const completedCount = eligibleGroups.reduce((acc, group) => acc + group.completedCount, 0);
        const remainingCount = Math.max(totalCount - completedCount, 0);

        return { totalCount, completedCount, remainingCount };
    }, [eligibleGroups]);

    return {
        questState,
        questGroups: eligibleGroups,
        totals,
        updateQuestStatus: markQuestCompleted,
        setGroupSkippedOrCompleted,
        isLoading
    };
};
